import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  Baby, 
  Info,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useStore } from '../store/useStore';

interface TextureGuideModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  ageInMonths: number;
}

const textures = [
  {
    age: "6-8 Bulan",
    texture: "Puree / Lumat Halus",
    description: "Makanan disaring atau diblender halus sampai tidak ada gumpalan.",
    tips: "Mulai dengan 2-3 sdm, perkenalkan rasa satu per satu.",
    icon: "🥣"
  },
  {
    age: "9-11 Bulan",
    texture: "Cincang Halus / Mashed",
    description: "Makanan dicincang halus (minced) atau dilumatkan kasar (mashed).",
    tips: "Mulai perkenalkan finger foods yang empuk agar Adek belajar menggigit.",
    icon: "🍲"
  },
  {
    age: "12-23 Bulan",
    texture: "Makanan Keluarga",
    description: "Menu yang sama dengan keluarga, hanya saja rasa tajam (pedas) dihindari.",
    tips: "Pastikan porsi gizi tetap seimbang (4 bintang).",
    icon: "🍽️"
  }
];

export default function TextureGuideModal({ isOpen, onOpenChange, ageInMonths }: TextureGuideModalProps) {
  const { babyProfile } = useStore();
  const currentTexture = ageInMonths < 9 ? textures[0] : ageInMonths < 12 ? textures[1] : textures[2];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] rounded-3xl p-0 overflow-hidden border-none shadow-2xl">
        <div className="bg-amber-600 p-6 text-white text-center space-y-2">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
            <Baby className="h-8 w-8" />
          </div>
          <DialogTitle className="text-xl font-bold">Panduan Tekstur Adek</DialogTitle>
          <DialogDescription className="text-white/80 text-xs">
            Standar pemberian makan WHO & IDAI untuk usia {ageInMonths} bulan.
          </DialogDescription>
        </div>

        <ScrollArea className="p-6 max-h-[60vh]">
          <div className="space-y-6">
            <section className="bg-amber-50 border border-amber-100 rounded-2xl p-5 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-amber-600" />
                <h3 className="text-sm font-bold text-amber-900 uppercase tracking-wider">Rekomendasi Saat Ini</h3>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-3xl">{currentTexture.icon}</span>
                <div>
                  <p className="font-bold text-stone-900">{currentTexture.texture}</p>
                  <p className="text-[10px] text-stone-500 font-medium">Usia {currentTexture.age}</p>
                </div>
              </div>
              <p className="text-xs text-stone-600 leading-relaxed italic">
                {currentTexture.description}
              </p>
            </section>

            <div className="space-y-4">
              <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.2em]">Tahapan Tekstur Lainnya</h4>
              {textures.map((t, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-2xl border border-stone-100 hover:bg-stone-50 transition-colors">
                  <span className="text-2xl pt-1">{t.icon}</span>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-stone-900 text-sm">{t.texture}</p>
                      <Badge variant="outline" className="text-[8px] py-0 h-4">{t.age}</Badge>
                    </div>
                    <p className="text-[10px] text-stone-500 leading-relaxed">{t.tips}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-stone-900 rounded-2xl p-4 flex gap-3 items-start">
               <Info className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
               <p className="text-[10px] text-white/80 leading-relaxed">
                 Ingat {babyProfile?.caregiverName || 'Mama'}, setiap anak unik. Jika Adek belum siap naik tekstur, jangan dipaksa ya. Konsultasikan dengan DSA jika ada kendala pertumbuhan.
               </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
